import React from 'react';
import { useTranslation } from 'react-i18next';
import { SaveOutlined } from '@ant-design/icons';
import { Col, Form, Input, Modal, Row, Select, Spin, message } from 'antd';
import AppDatePicker from '@/components/FormItems/DatePicker';
import { ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import { BlobStorageContainerType, GenderType, IdentityDocumentType } from '@/constants';
import { defaultApiErrorHandle } from '@/helpers/errorHandler';
import SingleImageUpload from '@/components/FormItems/SingleImageUpload';
import { PassengerIdentityDetailModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
import CountryInput from '@/components/FormItems/CountryInput';
import CountryCodeInput from '@/components/FormItems/CountryCodeInput';

type Props = {
  ticketId: string;
  onClose: (reload: boolean) => void;
};

export default function UpdatePassengerIdentityModal({ ticketId, onClose }: Props) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [passengerDetail, setPassengerDetail] = React.useState<PassengerIdentityDetailModel>();

  React.useEffect(() => {
    if (ticketId) {
      apiSdk.BookingDetailService.getTicketPassengerIdentity({ ticketId: ticketId })
        .then(res => {
          setPassengerDetail(res);
        })
        .catch(message.error)
        .finally(() => {
          setLoading(false);
        });
    }
  }, [ticketId]);

  React.useEffect(() => {
    if (passengerDetail) {
      form.setFieldsValue(passengerDetail);
    }
  }, [passengerDetail]);

  const updatePassenger = (values: any) => {
    setLoading(true);
    apiSdk.BookingDetailService.updateTicketPassengerIdentity({ body: { passengerId: passengerDetail?.passengerId, ...values } })
      .then(res => {
        if (res.success) {
          onClose(true);
        } else {
          message.error(res.message);
        }
      })
      .catch(defaultApiErrorHandle)
      .finally(() => {
        setLoading(false);
      });
  };
  return (
    <Modal
      open={true}
      maskClosable={false}
      width={ModalWidth.medium}
      style={{ top: 10 }}
      className="nta-custom-form"
      title={t('manageTicket_UpdatePassengerIdentity')}
      forceRender={true}
      okText={t('save')}
      cancelText={t('cancel')}
      okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
      onCancel={() => {
        onClose(false);
      }}
      onOk={() => {
        form
          .validateFields()
          .then(values => {
            updatePassenger(values);
          })
          .catch(info => {
            console.log(info);
          });
      }}
    >
      <Spin spinning={loading}>
        <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 6 }}>
          <Form.Item name="id" className="hidden">
            <Input type="hidden" />
          </Form.Item>

          <Row gutter={ROW_GUTTER}>
            <Col md={24}>
              <Form.Item label={t('manageTicket_UploadDocuments')} name="uploadDocuments">
                <SingleImageUpload multiple blobStorageContainer={BlobStorageContainerType.TransportPassengers} />
              </Form.Item>
            </Col>
            <Col md={24}>
              <Form.Item label={t('Identity type')} name="identityType" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Select options={IdentityDocumentType.__getSelectOptions()} />
              </Form.Item>
              <Form.Item label={t('Document number')} name="documentNumber" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Input />
              </Form.Item>
              <Form.Item label={t('base.CountryCode')} name="countryCode" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <CountryCodeInput className="w-100" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
}
